import React, { useRef, useEffect } from 'react';
import { SeeMoreButton } from './see-more-button';
import { useMediaQuery } from '../hooks/utils/useMediaQuery';

interface ContentSectionProps<T> {
  title: string;
  titleCount?: number;
  items: T[];
  itemsShown?: number;
  totalCount?: number;
  onLoadMore?: () => void;
  renderItem: (item: T | undefined, index: number) => React.ReactNode;
  shouldShow?: boolean;
  className?: string;
  isLoading?: boolean;
  skeletonCount?: number;
  layout?: 'carousel' | 'list';
}

export const ContentSection = <T,>({
  title,
  titleCount,
  items,
  itemsShown,
  totalCount,
  onLoadMore,
  renderItem,
  shouldShow = true,
  className = '',
  isLoading = false,
  skeletonCount = 6,
  layout = 'carousel',
}: ContentSectionProps<T>): React.ReactElement | null => {
  const dfMdMedia = useMediaQuery('md');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const useMobileCarousel = layout === 'carousel' && !dfMdMedia;

  const displayItems =
    isLoading && items.length === 0
      ? (Array(skeletonCount).fill(undefined) as (T | undefined)[]) // Show skeletons when loading from backend
      : useMobileCarousel && itemsShown !== undefined && items.length > itemsShown
        ? items // Mobile carousel: show all pre-loaded items
        : itemsShown !== undefined
          ? items.slice(0, itemsShown) // Desktop / list: limit to itemsShown
          : items; // Show all items

  const showSeeMore =
    onLoadMore &&
    (isLoading ||
      (itemsShown !== undefined && items.length > itemsShown) ||
      (totalCount !== undefined && itemsShown !== undefined && itemsShown < totalCount));

  // Infinite scroll for mobile: auto-load more when user scrolls near the end
  useEffect(() => {
    if (layout === 'list' || dfMdMedia || !onLoadMore || !showSeeMore || isLoading) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && onLoadMore) {
          onLoadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '400px',
        threshold: 0.01,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [dfMdMedia, onLoadMore, showSeeMore, isLoading, layout]);

  if (!shouldShow) {
    return null;
  }

  const titleWithCount =
    titleCount !== undefined ? (
      <>
        {title}{' '}
        <span className="text-sm font-normal text-gray-500">
          ({titleCount} {titleCount === 1 ? 'result' : 'results'})
        </span>
      </>
    ) : (
      title
    );

  const seeMore = showSeeMore ? (
    <div className="mt-6 flex w-full justify-center">
      <SeeMoreButton
        onClick={onLoadMore}
        buttonClassName="bg-button-secondary hover:bg-button-secondary-hover text-primary"
      />
    </div>
  ) : null;

  if (layout === 'list') {
    return (
      <div className={`mb-12 ${className}`}>
        <h2 className="font-albert-sans mb-4 text-xl font-medium leading-8 text-black">{titleWithCount}</h2>
        <div className="grid grid-cols-1 gap-x-6 gap-y-12 lg:grid-cols-2">
          {displayItems.map((item, index) => (
            <div key={index} className="min-w-0">
              {renderItem(item, index)}
            </div>
          ))}
        </div>
        {seeMore}
      </div>
    );
  }

  return (
    <div className={`mb-12 ${className}`}>
      <h2 className="font-albert-sans mb-4 text-xl font-medium leading-8 text-black">{titleWithCount}</h2>
      {dfMdMedia ? (
        // Desktop layout: flex-wrap grid
        <>
          <div className="flex flex-wrap items-start gap-x-6 gap-y-12">
            {displayItems.map((item, index) => renderItem(item, index))}
          </div>
          {showSeeMore && seeMore}
        </>
      ) : (
        <div
          ref={scrollContainerRef}
          className="h-124 flex gap-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {displayItems.map((item, index) => (
            <div key={index} data-carousel-item className="flex-shrink-0">
              {renderItem(item, index)}
            </div>
          ))}
          {/* Show skeletons when fetching from backend (not when revealing pre-loaded items) */}
          {isLoading &&
            items.length > 0 &&
            Array(Math.min(3, skeletonCount))
              .fill(undefined)
              .map((_, index) => (
                <div key={`skeleton-${index}`} data-carousel-item className="flex-shrink-0">
                  {renderItem(undefined, displayItems.length + index)}
                </div>
              ))}
          {/* Sentinel element for infinite scroll detection */}
          {showSeeMore && (
            <div
              ref={sentinelRef}
              data-carousel-item
              className="flex-shrink-0"
              style={{ width: '1px', height: '1px', minWidth: '1px', minHeight: '1px' }}
              aria-hidden="true"
            />
          )}
        </div>
      )}
    </div>
  );
};
